/**
 * Карточка пина `/pins/:pinId`: оригинал, метрики, теги, «найден в», заметка
 * сцены и вход в примерку. Тот же `PinInfo`, что в просмотре пина, — без
 * «N из M»: листать тут нечего.
 *
 * Теги правятся локальными мутациями с патчем кэша карточки: перезапрашивать
 * ради одной метки незачем. Заметка сцены («что важно в сцене») уходит в
 * промпт примерки; пустая — допустима, сохраняется по blur и Ctrl+Enter.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { FittingFeed } from "@/components/fitting/FittingFeed";
import { PinInfo, PinPicture } from "@/components/trends/PinViewer";
import { btnGhost, btnPrimary, Empty, ErrorLine, input, label, Loading, Panel } from "@/components/trends/ui";
import { fittingKeys, fittingPath, loadPin, saveSceneNote, type PinCard } from "@/lib/fitting";
import { errorText } from "@/lib/project";
import { useCreateVideoRef } from "@/lib/videoRefApi";
import { dressTitle } from "@/lib/photos";
import { loadTags, patchPinTags, tagKeys, tagPins, untagPins, type TagBy } from "@/lib/tags";
import { retryQuery } from "@/lib/trends";

const NOTE_MAX = 500;

export function PinPage() {
  const id = Number(useParams().pinId);
  const valid = Number.isInteger(id) && id > 0;
  const pin = useQuery({
    queryKey: fittingKeys.pin(id),
    queryFn: () => loadPin(id),
    retry: retryQuery,
    enabled: valid,
  });

  if (!valid)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес пина.")} />
      </div>
    );
  if (pin.isPending) return <Loading />;
  if (pin.isError)
    return (
      <div className="grid gap-3 p-5">
        <Link to="/trends" className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg">
          <ArrowLeft size={13} />
          Все тематики
        </Link>
        <ErrorLine error={pin.error} />
      </div>
    );
  return <PinView pin={pin.data} />;
}

function PinView({ pin }: { pin: PinCard }) {
  const queryClient = useQueryClient();
  const key = fittingKeys.pin(pin.id);
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const tags = tagsQuery.data?.tags ?? [];
  const [lastLibraryId, setLastLibraryId] = useState<number | null>(null);

  const patch = (tagId: number, add: boolean) =>
    queryClient.setQueryData<PinCard>(key, (old) => {
      const got = patchPinTags(old && { ...old, pins: [old] }, new Set([pin.id]), tagId, add);
      return got?.pins[0] as PinCard | undefined;
    });

  const tag = useMutation({
    mutationFn: (by: TagBy) => tagPins([pin.id], by),
    onSuccess: (got) => {
      patch(got.tag.id, true);
      if (got.tag.created) void queryClient.invalidateQueries({ queryKey: tagKeys.list });
    },
  });
  const untag = useMutation({
    mutationFn: (tagId: number) => untagPins([pin.id], tagId),
    onSuccess: (_got, tagId) => patch(tagId, false),
  });

  const picture = pin.asset_path ?? pin.thumb_path;
  const caption =
    pin.kind === "video"
      ? "видео: в примерку идёт обложка"
      : !pin.has_image_url
        ? "у пина нет адреса оригинала"
        : !pin.asset_path
          ? "оригинал не скачан — скачается при первой примерке"
          : null;

  return (
    <div className="mx-auto grid max-w-[1200px] gap-4 p-5" data-annot="карточка пина">
      <Link
        to="/trends"
        className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ArrowLeft size={13} />
        Все тематики
      </Link>

      <div className="grid items-start gap-5 md:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid justify-items-center gap-2">
          {picture ? (
            <PinPicture pin={pin} />
          ) : (
            <Empty>Картинка пина не скачана.</Empty>
          )}
          {caption ? <div className="text-[12px] text-fg-faint">{caption}</div> : null}
        </div>

        <div className="grid gap-3 text-[12.5px]">
          <PinInfo
            pin={pin}
            defaultLibraryName={pin.title || `Пин ${pin.code}`}
            lastLibraryId={lastLibraryId}
            onAdded={(libraryId) => {
              setLastLibraryId(libraryId);
              void queryClient.invalidateQueries({ queryKey: key });
            }}
            tags={tags}
            onTag={(_ids, by) => tag.mutateAsync(by).then(() => undefined)}
            onUntag={(_ids, tagId) => untag.mutateAsync(tagId).then(() => undefined)}
          />

          <FoundIn pin={pin} />
          <SceneNote pin={pin} />

          <Link to={fittingPath({ pins: [pin.id] })} className={`${btnPrimary} w-fit`}>
            Генерировать
          </Link>
          <VideoRefEntry pin={pin} />
        </div>
      </div>

      <Panel title="Примерки">
        <FittingFeed filter={{ pins: [pin.id] }} seed={pin.fittings} emptyText="Этот пин ещё не примеряли" />
      </Panel>
    </div>
  );
}

/** Вход в видео-референс: видео-пин → разобрать; уже разобран — ссылка на референс. */
function VideoRefEntry({ pin }: { pin: PinCard }) {
  const navigate = useNavigate();
  const create = useCreateVideoRef();
  if (pin.video_ref_id !== null)
    return (
      <Link to={`/video-refs/${pin.video_ref_id}`} className="w-fit text-[12.5px] text-accent-text hover:underline" data-annot="пин: референс видео">
        референс {pin.video_ref_id} ↗
      </Link>
    );
  if (!pin.video_path && pin.kind !== "video") return null;
  return (
    <div className="grid gap-1">
      <button
        type="button"
        className={`${btnGhost} w-fit`}
        disabled={create.isPending}
        data-annot="пин: разобрать видео"
        onClick={() =>
          create.mutate(
            { pin_id: pin.id },
            { onSuccess: (got) => navigate(`/video-refs/${got.ref.ref.id}`, { state: got.existing ? { existing: true } : undefined }) },
          )
        }
      >
        {create.isPending ? "Создаю…" : "Разобрать видео"}
      </button>
      {create.isError ? <div className="text-[12px] text-err">{errorText(create.error)}</div> : null}
    </div>
  );
}

/** «Найден в»: подборки со словами и платья, чьи «Похожие» вернули пин. */
function FoundIn({ pin }: { pin: PinCard }) {
  const { topics, dresses } = pin.found_in;
  return (
    <div className="grid gap-1.5 border-t border-border pt-3" data-annot="найден в">
      <div className="text-[12.5px] font-medium text-fg">Найден в</div>
      {topics.length || dresses.length ? (
        <ul className="grid gap-1 text-fg-muted">
          {topics.map((t) => (
            <li key={`${t.topic_id}-${t.query_id}`}>
              <Link to={`/trends/${t.topic_id}`} className="text-accent-text hover:underline">
                {t.topic_name}
              </Link>
              {" · "}
              <Link to={`/trends/type/${t.query_id}`} className="text-accent-text hover:underline">
                {t.query_name}
              </Link>
            </li>
          ))}
          {dresses.map((d) => (
            <li key={`d${d.dress_id}`}>
              платье{" "}
              <Link to={`/photos/${d.dress_id}`} className="text-accent-text hover:underline">
                {dressTitle({ id: d.dress_id, name: d.dress_name })}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[11.5px] text-fg-faint">Нигде не найден.</div>
      )}
    </div>
  );
}

/** «Что важно в сцене»: сохраняется по blur и Ctrl+Enter, пустая — допустима. */
function SceneNote({ pin }: { pin: PinCard }) {
  const queryClient = useQueryClient();
  const saved = pin.scene_note ?? "";
  const [text, setText] = useState(saved);
  useEffect(() => setText(saved), [saved]);

  const save = useMutation({
    mutationFn: (value: string) => saveSceneNote(pin.id, value),
    onSuccess: (got) =>
      queryClient.setQueryData<PinCard>(fittingKeys.pin(pin.id), (old) =>
        old ? { ...old, scene_note: got.scene_note } : old,
      ),
  });
  const commit = () => {
    if (text.trim() === saved.trim() || save.isPending) return;
    save.mutate(text.trim());
  };

  return (
    <div className="grid gap-1.5 border-t border-border pt-3" data-annot="заметка сцены">
      <label htmlFor="scene-note" className="text-[12.5px] font-medium text-fg">
        Что важно в сцене
      </label>
      <textarea
        id="scene-note"
        className={`${input} min-h-[72px] w-full resize-y`}
        value={text}
        maxLength={NOTE_MAX}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            commit();
          }
        }}
      />
      <div className={label}>
        уйдёт в промпт примерки; можно оставить пустым · {text.length}/{NOTE_MAX}
        {save.isPending ? " · сохраняю…" : ""}
      </div>
      <ErrorLine error={save.error} />
    </div>
  );
}
